import { observable } from 'mobx';
import { MissedCall } from './MissedCall';
import { ApiService } from '../services/api';
import { RegisteredCall } from './RegisteredCall';
import { Resource } from '../services/json-api/Resource';
import { JsonDocumentInterface } from '../services/json-api/document';
import { StartedCall } from './StartedCall';
import { CallType } from './CallType';
import { ResponseStatus } from '../services/api/response/ResponseStatus';
import { JsonDocument } from '../services/json-api/JsonDocument';
import { JsonApiService } from '../services/json-api/JsonApiService';
import { voiceLogStore, VoiceLogStore } from './voiceLogStore';
import config from '../../config';

export class CallStore {
    @observable unseen = 0;
    @observable private _currentCall?: Resource<RegisteredCall>;
    @observable private _history?: JsonDocument<RegisteredCall>;
    @observable historyLoading = false;
    private api: ApiService;
    private jsonApi: JsonApiService;
    private voiceLogStore: VoiceLogStore;

    public static UNKNOWN = 'onbekend';
    public static ANONYMOUS = 'anonymous';

    public constructor() {
        this.api = new ApiService();
        this.jsonApi = new JsonApiService();
        this.voiceLogStore = voiceLogStore;
    }

    public get currentCall(): Resource<RegisteredCall> | undefined {
        return this._currentCall;
    }

    public get history(): JsonDocument<RegisteredCall> | undefined {
        return this._history;
    }

    public async getHistory(): Promise<JsonDocument<RegisteredCall> | null> {
        this.historyLoading = true;

        const response = await this.api.get<JsonDocumentInterface<RegisteredCall>>('api/v2/call-registration');

        this.historyLoading = false;

        if (response.getStatus() === ResponseStatus.OK) {
            const document = new JsonDocument<RegisteredCall>(
                response.getData() as JsonDocumentInterface<RegisteredCall>
            );

            this._history = document;

            return document;
        }

        return null;
    }

    public addMissedCall(event: SIPml.Session): void {
        this.unseen += 1;
        this.missedCall(event)
    }

    public async startCall(event: SIPml.Session, previousCall?: Resource<RegisteredCall>): Promise<void> {
        const response = await this.api.post<RegisteredCall>(
            'api/v2/call-registration',
            Resource.makeCreateObject<StartedCall>('call-registration', {
                phone_number: event.getRemoteFriendlyName(),
                call_type_id: CallType.OUTGOING,
                call_registration_parent_uuid: previousCall ? previousCall.getId() : undefined,
                started: true,
                is_missed_call: false
            })
        );

        if (response.getStatus() === ResponseStatus.CREATED) {
            this._currentCall = new JsonDocument<RegisteredCall>(
                response.getData() as JsonDocumentInterface<RegisteredCall>
            ).first() as Resource<RegisteredCall>;
        }
    }

    public async endCurrentCall(): Promise<void> {
        if (this._currentCall) {
            if (this.voiceLogStore.voiceLog) {
                await this.voiceLogStore.endVoiceLog(this._currentCall);
            }

            fetch(`${config.api.baseUrl}api/v2/call-registration/${this._currentCall.getId()}`, {
                method: 'PATCH',
                body: JSON.stringify(this._currentCall.makeUpdateObject({
                    ended: true
                })),
                keepalive: true,
                headers: this.api.getHeaders()
            }).then(() => {
                this._currentCall = undefined;
            })

        }
    }

    public isAnonymous(session: SIPml.Session): boolean {
        return session.getRemoteFriendlyName().toLowerCase() === 'anonymous';
    }

    public async missedCall(session: SIPml.Session): Promise<void> {
        return fetch(`${config.api.baseUrl}api/v2/call-registration`, {
            method: 'POST',
            body: JSON.stringify(Resource.makeCreateObject<MissedCall>('call-registration', {
                phone_number: this.isAnonymous(session) ? null : session.getRemoteFriendlyName(),
                anonymous: this.isAnonymous(session),
                call_type_id: CallType.INCOMING,
                started: false,
                is_missed_call: true
            })),
            keepalive: true,
            headers: this.api.getHeaders()
        }).then(() => {
            this.getHistory();
        });
    }
}

export const callStore = new CallStore();
